import React, { FC, useEffect } from 'react';
import { uniqBy } from 'lodash';
import { ApplicantModal } from '@src/containers/ApplicantModal';
import {
  CompanyProfileStateEnum,
  useGetVerifiedUserForFeaturedTalentLazyQuery,
  useSessionQuery,
} from '@src/graphql/generated';
import { KeyboardNotice } from '@src/components/KeyboardNotice';
import { LocalStorageKeys } from '@src/enums/localStorageKeys.enum';
import { MetricContext } from '@src/metrics/metrics.enum';
import { ConnectParams } from '@src/containers/Source/TalentCard/interfaces/connectParams.interface';
import { useSearchVerifiedUsers } from '../hooks';
import { QueryParams } from '../interfaces/queryParams.interface';

interface ModalProps {
  excludedUserIds: string[];
  invitedUserIds: string[];
  isDirectConnectShownDefault?: boolean;
  onCloseModal: () => void;
  onConfirmTalent: (userId: string, params?: ConnectParams) => void;
  queryParams: QueryParams;
  recruiterCurrencyCode: string;
  userId: string;
}

export const Modal: FC<ModalProps> = ({
  recruiterCurrencyCode,
  isDirectConnectShownDefault,
  queryParams,
  excludedUserIds,
  invitedUserIds,
  userId,
  onConfirmTalent,
  onCloseModal,
}) => {
  const { data: sessionData } = useSessionQuery();

  const companyProfile = sessionData?.company.profile;

  const [fetchVerifiedUser, { data: verifiedUserData, loading: isSingleUserDataLoading }] =
    useGetVerifiedUserForFeaturedTalentLazyQuery({
      fetchPolicy: 'network-only',
    });

  const isPublished = companyProfile?.state === CompanyProfileStateEnum.PUBLISHED;

  const { feed, isInitialLoading, isLoading, hasMoreResults, fetchMore } = useSearchVerifiedUsers({
    excludedUserIds: excludedUserIds,
    queryParams: queryParams,
    recruiterCurrencyCode: recruiterCurrencyCode,
  });

  const applicants = !isPublished ? feed.slice(0, 3) : feed;

  useEffect(() => {
    if (queryParams.userId) {
      fetchVerifiedUser({
        variables: {
          recruiterCurrencyCode: recruiterCurrencyCode,
          userId: queryParams.userId,
        },
      });
    }
  }, [queryParams.userId]);

  const feedData =
    !(isLoading || isSingleUserDataLoading) &&
    verifiedUserData &&
    !verifiedUserData?.verifiedUser.recruiterState.hasSeen &&
    !applicants.find(applicant => applicant.user.id === queryParams.userId)
      ? uniqBy([verifiedUserData?.verifiedUser, ...applicants], 'user.id')
      : applicants;

  return (
    <div>
      <KeyboardNotice localStorageKey={LocalStorageKeys.FEATURED_APPLICANTS_KEYBOARD_SHORTCUTS_NOTICE_SEEN_COUNT} />

      <ApplicantModal
        activeUserId={userId}
        feed={feedData}
        fetchMore={fetchMore}
        hasMoreResults={hasMoreResults}
        invitedUserIds={invitedUserIds}
        isDirectConnectShownDefault={isDirectConnectShownDefault}
        isFeaturedApplicantModal={true}
        isInitialLoading={isInitialLoading || isSingleUserDataLoading}
        isLoading={isLoading || isSingleUserDataLoading}
        metricContext={MetricContext.FEATURED_APPLICANTS_VIEW}
        onCloseModal={onCloseModal}
        onConfirmTalent={onConfirmTalent}
      />
    </div>
  );
};
